import { loadScript } from 'widgets/toolbox/util';

import { TInputHidden } from 'widgets/forms/InputHidden';

type TGrecaptchaInstance = typeof window.grecaptcha & {
    /**
     * Programmatically invoke the reCAPTCHA V3 check. Used if the invisible reCAPTCHA is on a div instead of a button.
     *
     * @param opt_widget_id Optional widget ID, defaults to the first widget created if unspecified.
     * @param action the action
     *
     * @returns a promise-like object containing the token
     */
    // TODO: Remove this fix after @types/grecaptcha v3.0.4 updates to reCAPTCHA to v3
    execute(opt_widget_id?: number, action?: ReCaptchaV2.Action): Promise<string>;
};

/**
 * @description Google Recaptcha Widget Implementation
 * @param InputHidden Base widget for extending
 */
function GoogleRecaptchaClassCreator(InputHidden: TInputHidden) {
    /**
     * @category widgets
     * @subcategory forms
     * @class GoogleRecaptcha
     * @augments TInputHidden
     * @classdesc Google Recaptcha field component with next features:
     * 1. Loads Google Recaptcha script and render recaptcha
     * 2. Executes recaptcha on field validation
     * 3. Handles errors and render error message
     * 4. Handles recaptcha token expiration
     * @property {string} data-widget - Widget name `googleRecaptcha`
     * @property {string} data-site-key - Site key for the Google Recaptcha API
     * @property {string} data-recaptcha-error-message - Error message
     * @property {boolean} data-recaptcha-enabled - Determines if Recaptcha functionality enabled
     * @example <caption>Example of GoogleRecaptcha widget markup</caption>
     * <form
     *     ...
     *     method="POST"
     *     data-widget="ajaxform"
     *     data-event-submit.prevent="handleSubmit"
     * >
     *     ...
     *     <isprint value="${
     *         formElement(pdict.trackOrderForm.googleRecaptcha)
     *         .setData('widget.attributes.data-site-key', require('util/sitepref').getValue('googleCaptchaKey'))
     *         .setData('widget.attributes.data-recaptcha-enabled', googleRecaptchaHelper.isEnabled())
     *         .setData('widget.attributes.data-recaptcha-error-message', Resource.msg('form.recaptcha.error', 'forms', null))
     *         .render()
     *     }" encoding="off"/>
     *     ...
     * </form>
     */
    class GoogleRecaptcha extends InputHidden {
        grecaptcha?: TGrecaptchaInstance;

        recaptchaWidgetId?: number;

        prefs() {
            return {
                siteKey: '',
                dataSize: <ReCaptchaV2.Size> 'invisible',
                recaptchaScriptUrl: 'https://www.google.com/recaptcha/api.js',
                recaptchaErrorMessage: '',
                recaptchaContainerClass: 'g-recaptcha',
                recaptchaEnabled: false,
                recaptchaAction: '',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init() {
            super.init();
            this.initializeRecaptcha();
        }

        /**
         * @description Load Google Recaptcha script
         * @param url Url to load Google Recaptcha script
         * @returns Promise
         */
        loadRecaptchaScript(url: string) {
            if (window.grecaptcha) {
                return Promise.resolve(window.grecaptcha);
            }

            return loadScript<TGrecaptchaInstance>(url, {
                globalScriptName: 'grecaptcha'
            });
        }

        /**
         * @description Load Google Captcha script and render captcha
         */
        initializeRecaptcha() {
            if (!this.prefs().recaptchaEnabled) {
                this.ref('self').remove();
            }

            // will resolve instantly and not load recaptcha again
            // if loaded by another instance
            this.loadRecaptchaScript(this.prefs().recaptchaScriptUrl).then((grecaptcha) => {
                grecaptcha?.ready(() => {
                    this.grecaptcha = grecaptcha;

                    try {
                        const container = this.appendContainer();

                        const id = grecaptcha.render(container, {
                            sitekey: this.prefs().siteKey,
                            size: this.prefs().dataSize,
                            'expired-callback': this.onRecaptchaReset.bind(this),
                            'error-callback': this.onRecaptchaError.bind(this)
                        });

                        this.recaptchaWidgetId = id;
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (error) {
                        this.onRecaptchaError();
                    }
                });
            });
        }

        /**
         * @description Appends recaptcha container div
         * @returns Container element
         */
        appendContainer(): HTMLElement {
            const self = this.ref('self').get();

            if (!self) {
                throw new Error('Cannot append reCAPTCHA container! Ref \'self\' is empty.');
            }

            const container = document.createElement('div');

            container.classList.add(this.prefs().recaptchaContainerClass);

            /**
             * First invisible reCaptcha widget should be rendered in the container that always will be visible on the current page.
             * In this case, we're omitting adding the container in the form, because it could be hidden.
             */
            if (this.prefs().dataSize === 'invisible') {
                container.style.position = 'fixed';
                container.style.top = '50vh';
                container.style.left = '50vw';

                document.body.appendChild(container);
            } else {
                self.appendChild(container);
            }

            return container;
        }

        /**
         * @description Google Captcha success executing handler
         * @param token Google Captcha token
         */
        onRecaptchaSuccess(token: string) {
            this.ref('field').val(token);
        }

        /**
         * @description Google Captcha errors handler
         */
        onRecaptchaError() {
            this.emitWithoutContext('captchainiterror', {
                message: this.prefs().recaptchaErrorMessage,
                scrollToError: false
            });
        }

        /**
         * @description Google Captcha expiration handler
         */
        onRecaptchaReset() {
            this.ref('field').val('');

            this.grecaptcha?.reset(this.recaptchaWidgetId);
        }

        /**
         * @description Execute Google Recaptcha and returns promise
         * @returns Promise
         */
        executeRecaptcha(): Promise<string|null> {
            let executeOptions;

            if (this.prefs().recaptchaAction) {
                executeOptions = {
                    action: String(this.prefs().recaptchaAction)
                };
            }

            return new Promise((resolve, reject) => {
                this.grecaptcha?.execute(this.recaptchaWidgetId, executeOptions)
                    .then(token => {
                        this.onRecaptchaSuccess(token);
                        resolve(token);
                    })
                    .catch((error) => reject(error));
            });
        }

        /**
         * @description Reloaded method to disable scrolling on validation fail
         */
        setFocus() {
            // Override setFocus logic
        }
    }

    return GoogleRecaptcha;
}

export type TGoogleRecaptcha = ReturnType<typeof GoogleRecaptchaClassCreator>;

export type TGoogleRecaptchaInstance = InstanceType<TGoogleRecaptcha>;

export default GoogleRecaptchaClassCreator;
